import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { serverError } from "../../../../components/modals/payments/register/registerPaymentModal";

/*
	«Regalos» del editor de promos (docs/ESPEC_PROMO_REGALOS.md, reemplaza a «Kit Que Regala»): una promo de adelanto puede regalar
	productos del catálogo además de lo que ya da (meses, 2x1, todas juntas); una promo de kit es la que solo regala productos.
	La promo ofrece una o varias ALTERNATIVAS (Kit Básico o Kit Premium o Kit + Uniforme); cada una dice a qué cursos de la promo le toca
	(vacío = a todos) y qué productos da, con cantidad. Si a un curso le toca más de una, Caja elige al aplicar. La talla o el color NO se
	eligen aquí: se eligen en la entrega. El editor guarda `giftOptions` = { v: 1, options: [{ key, label, courseIds, items: [{ articleId,
	quantity }] }] } junto con el resto de la promo; el servidor normaliza, valida y pone key / label si faltan.
	Buscador: GET promotions/_kitArticles?q= · Sugerir: GET promotions/_giftSuggestions (kits de cada curso, rt_kit_courses).
*/

const http = HttpManager.getInstance();
const num = (n: number) => (Math.round((Number(n) || 0) * 100) / 100).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Monto con el símbolo del plantel: «$1,200.00» en pesos, «L 1,200.00» en lempiras. */
const monto = (sym: string, n: number) => (/^[A-Za-z]/.test(sym) ? sym + " " : sym) + num(n);

export const MAX_ALTERNATIVAS = 30;
export const MAX_PRODUCTOS = 10;
export const MAX_CANTIDAD = 20;

export interface RegaloItem { articleId: number; quantity: number; name: string; price: number | null; sku?: string | null; kind?: string | null }
export interface Alternativa {
	/** Solo para React (no se guarda). */
	uid: string;
	/** La del servidor («o1»…); las nuevas van sin key y el servidor la pone. */
	key?: string;
	label: string;
	courseIds: number[];
	items: RegaloItem[];
	/** Nombres de cursos que mandó el servidor (por si el curso ya no está en el catálogo del plantel). */
	courseNames?: Record<number, string>;
}

let seq = 0;
const uid = () => "a" + (++seq) + "-" + Date.now().toString(36);

const aItem = (i: any): RegaloItem | null => {
	const id = Number(i?.articleId ?? i?.id);
	if (!(id > 0)) return null;
	return { articleId: id, quantity: Math.max(1, Math.min(MAX_CANTIDAD, Number(i?.quantity) || 1)), name: String(i?.name || "Producto #" + id),
		price: i?.price !== undefined && i?.price !== null && i?.price !== "" ? Number(i.price) : null, sku: i?.sku ?? i?.code ?? null, kind: i?.kind ?? null };
};

/** Las alternativas de una promo guardada: `giftOptions` (nuevo) o, de una promo vieja, su único `giftArticle`. Nunca truena si falta algo. */
export const regalosDePromo = (p: any): Alternativa[] => {
	const opts = Array.isArray(p?.giftOptions?.options) ? p.giftOptions.options : Array.isArray(p?.giftOptions) ? p.giftOptions : null;
	if (opts) {
		return opts.map((o: any) => {
			const ids: number[] = Array.isArray(o?.courseIds) ? o.courseIds.map(Number).filter((x: number) => x > 0) : [];
			const names: Record<number, string> = {};
			// El servidor manda { courseId: nombre }; un arreglo (mismo orden que courseIds) queda como respaldo.
			if (o?.courseNames && typeof o.courseNames === "object" && !Array.isArray(o.courseNames)) Object.keys(o.courseNames).forEach((k) => { if (Number(k) > 0 && o.courseNames[k]) names[Number(k)] = String(o.courseNames[k]); });
			else if (Array.isArray(o?.courseNames)) o.courseNames.forEach((n: any, i: number) => { if (ids[i] && n) names[ids[i]] = String(n); });
			return { uid: uid(), key: o?.key ? String(o.key) : undefined, label: String(o?.label || ""), courseIds: ids,
				items: (Array.isArray(o?.items) ? o.items : []).map(aItem).filter(Boolean) as RegaloItem[], courseNames: names };
		});
	}
	const id = p?.giftArticle?.id ?? p?.giftArticleId;
	if (id) return [{ uid: uid(), label: "", courseIds: [], items: [aItem({ articleId: id, quantity: 1, name: p?.giftArticle?.name, price: p?.giftArticle?.price })!] }];
	return [];
};

/** Lo que vale una alternativa: Σ precio × cantidad (los que no tienen precio cuentan 0). */
export const valorAlternativa = (a: Alternativa): number => a.items.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 0), 0);
/** Nombre que verá Caja: el escrito o, si no, los productos unidos con « + ». */
export const nombreAlternativa = (a: Alternativa): string =>
	a.label.trim() || a.items.map((i) => (i.quantity > 1 ? i.quantity + " × " : "") + i.name).join(" + ") || "Sin Productos";

/** Lo que se manda al guardar (null = la promo no regala productos). */
export const giftOptionsPayload = (alts: Alternativa[]): { v: 1; options: any[] } | null => !alts.length ? null : ({
	v: 1,
	options: alts.map((a) => ({ ...(a.key ? { key: a.key } : {}), label: a.label.trim(), courseIds: a.courseIds,
		items: a.items.map((i) => ({ articleId: i.articleId, quantity: i.quantity })) }))
});

/** Lo que falta en los regalos para poder guardar (frases para «Para guardar falta: …»). `cubre` = cursos que cubre la promo (null = no se sabe / todos). */
export const faltanRegalos = (alts: Alternativa[], requerido: boolean, cubre: number[] | null): string[] => {
	const f: string[] = [];
	if (requerido && !alts.length) f.push("al menos una alternativa en Regalos (la promo de kit regala productos)");
	if (alts.length > MAX_ALTERNATIVAS) f.push("dejar " + MAX_ALTERNATIVAS + " alternativas o menos en Regalos");
	alts.forEach((a, n) => {
		const t = "la Alternativa " + (n + 1);
		if (!a.items.length) f.push("al menos un producto en " + t);
		if (a.items.length > MAX_PRODUCTOS) f.push(MAX_PRODUCTOS + " productos o menos en " + t);
		if (a.items.some((i) => !(i.quantity >= 1 && i.quantity <= MAX_CANTIDAD && Number.isInteger(i.quantity)))) f.push("cantidades de 1 a " + MAX_CANTIDAD + " en " + t);
		if (cubre && a.courseIds.some((c) => !cubre.includes(c))) f.push("quitar de " + t + " los cursos que la promo ya no cubre");
	});
	return f;
};

/** Buscador de productos del catálogo del plantel (espera a que termines de escribir). */
const BuscarProducto: React.FC<{ campusId: number; sym: string; disabled?: boolean; onPick: (i: RegaloItem) => void }> = ({ campusId, sym, disabled, onPick }) => {
	const [q, setQ] = useState("");
	const [lista, setLista] = useState<any[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		if (!q.trim()) { setLista(null); return; }
		let vigente = true;
		const t = setTimeout(() => {
			http.promoApplicationsService.kitArticles(campusId, q.trim())
				.then((r: any) => { if (vigente) { setLista(Array.isArray(r.data) ? r.data : (r.data?.articles || r.data?.data || [])); setError(null); } })
				.catch(() => { if (vigente) { setLista([]); setError("No se pudo buscar el producto."); } });
		}, 300);
		return () => { vigente = false; clearTimeout(t); };
	}, [campusId, q]);
	return (
		<div style={{ marginTop: 6 }}>
			<input className="form-control form-control-sm" style={{ maxWidth: 420 }} placeholder="Buscar Producto para Agregar (kit, uniforme, artículo)" value={q} disabled={disabled}
				onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") setQ(""); }} />
			{error ? <div className="wdOro" style={{ fontSize: 13 }}>{error}</div> : null}
			{lista && lista.length ? (
				<div className="wdCasillas" style={{ maxHeight: 200, maxWidth: 560 }}>
					{lista.map((k: any) => (
						<button key={"k" + k.id} type="button" className="wdFilaBoton wdProdOpcion" onClick={() => { const it = aItem({ ...k, quantity: 1 }); if (it) onPick(it); setQ(""); }}>
							<span>{k.name}{k.code || k.sku ? <small className="wdTenue"> · {k.code || k.sku}</small> : null}{k.kind && k.kind !== "kit" ? <small className="wdTenue"> · {String(k.kind)}</small> : null}</span>
							{k.price != null ? <span className="wdTenue">{monto(sym, Number(k.price))}</span> : null}
						</button>
					))}
				</div>
			) : lista ? <div className="wdTenue" style={{ fontSize: 13 }}>Sin productos con «{q.trim()}».</div> : null}
		</div>
	);
};

interface Props {
	campusId: number;
	sym: string;
	/** Promo guardada (para «Sugerir Kits por Curso» cuando el editor no tiene cursos marcados). */
	promotionId?: number | null;
	value: Alternativa[];
	onChange: (v: Alternativa[]) => void;
	/** Cursos que cubre la promo ahora mismo en el editor (los chips solo ofrecen estos). */
	cursosPromo: { id: number; name: string }[];
	/** Todos los cursos del plantel (para el nombre de un curso que ya no cubre la promo). */
	cursosPlantel: { id: number; name: string }[];
	/** Cursos a mandar a _giftSuggestions (vacío = los de la promo guardada / los del plantel). */
	cursosParaSugerir: number[];
	/** Promo de kit: sin al menos una alternativa no se guarda. */
	requerido: boolean;
}

const RegalosPromo: React.FC<Props> = ({ campusId, sym, promotionId, value, onChange, cursosPromo, cursosPlantel, cursosParaSugerir, requerido }) => {
	const [sugiriendo, setSugiriendo] = useState(false);
	const [aviso, setAviso] = useState<string | null>(null);
	const vivo = useRef(true);
	// StrictMode monta, desmonta y vuelve a montar: se vuelve a poner en true al montar.
	useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);
	const cubre = new Set(cursosPromo.map((c) => c.id));
	const nombreCurso = (a: Alternativa, id: number) => cursosPlantel.find((c) => c.id === id)?.name || cursosPromo.find((c) => c.id === id)?.name || a.courseNames?.[id] || "Curso " + id;
	const cambiar = (u: string, fn: (a: Alternativa) => Alternativa) => onChange(value.map((a) => (a.uid === u ? fn(a) : a)));
	const agregar = () => onChange([...value, { uid: uid(), label: "", courseIds: [], items: [] }]);
	const duplicar = (a: Alternativa) => {
		const i = value.findIndex((x) => x.uid === a.uid);
		const copia: Alternativa = { ...a, uid: uid(), key: undefined, label: a.label.trim() ? a.label.trim() + " (copia)" : "", items: a.items.map((x) => ({ ...x })), courseIds: [...a.courseIds] };
		onChange([...value.slice(0, i + 1), copia, ...value.slice(i + 1)]);
	};
	const borrar = (a: Alternativa) => onChange(value.filter((x) => x.uid !== a.uid));
	const agregarProducto = (a: Alternativa, it: RegaloItem) => cambiar(a.uid, (x) => {
		const ya = x.items.find((i) => i.articleId === it.articleId);
		if (ya) return { ...x, items: x.items.map((i) => (i.articleId === it.articleId ? { ...i, quantity: Math.min(MAX_CANTIDAD, i.quantity + 1) } : i)) };
		return x.items.length >= MAX_PRODUCTOS ? x : { ...x, items: [...x.items, it] };
	});

	const sugerir = async () => {
		try {
			setSugiriendo(true); setAviso(null);
			const r = await http.promoApplicationsService.giftSuggestions(campusId, cursosParaSugerir.length ? { courseIds: cursosParaSugerir } : { promotionId });
			const cursos: any[] = Array.isArray(r.data?.courses) ? r.data.courses : [];
			// Un kit que sirve a varios cursos queda en UNA alternativa con esos cursos (no repetida por curso).
			const porKit = new Map<number, { item: RegaloItem; courseIds: number[]; orden: number }>();
			let orden = 0;
			cursos.forEach((c) => (Array.isArray(c?.kits) ? [...c.kits] : []).sort((x: any, y: any) => (Number(x?.tierRank) || 0) - (Number(y?.tierRank) || 0)).forEach((k: any) => {
				const it = aItem({ ...k, quantity: 1 });
				if (!it) return;
				const cid = Number(c?.courseId);
				const ya = porKit.get(it.articleId);
				if (ya) { if (cid > 0 && !ya.courseIds.includes(cid)) ya.courseIds.push(cid); }
				else porKit.set(it.articleId, { item: it, courseIds: cid > 0 ? [cid] : [], orden: orden++ });
			}));
			// Lo que ya está como alternativa de un solo producto, con los mismos cursos, no se vuelve a agregar.
			const igual = (a: Alternativa, s: { item: RegaloItem; courseIds: number[] }) => a.items.length === 1 && a.items[0].articleId === s.item.articleId
				&& a.courseIds.length === s.courseIds.length && a.courseIds.every((x) => s.courseIds.includes(x));
			const nuevas: Alternativa[] = Array.from(porKit.values()).sort((a, b) => a.orden - b.orden).filter((s) => !value.some((a) => igual(a, s)))
				.map((s) => ({ uid: uid(), label: "", courseIds: s.courseIds, items: [s.item] }));
			if (!vivo.current) return;
			const caben = nuevas.slice(0, Math.max(0, MAX_ALTERNATIVAS - value.length));
			if (caben.length) onChange([...value, ...caben]);
			setAviso(!cursos.length || !porKit.size ? "Los cursos de la promo no tienen kits ligados en este plantel."
				: !nuevas.length ? "Esos kits ya están como alternativas."
				: "Se agregaron " + caben.length + (caben.length === 1 ? " alternativa" : " alternativas") + (caben.length < nuevas.length ? " (tope de " + MAX_ALTERNATIVAS + ")" : "") + ": borra las que no quieras.");
		} catch (e) {
			if (vivo.current) setAviso(serverError(e));
		} finally {
			if (vivo.current) setSugiriendo(false);
		}
	};

	return (
		<div className="wdRegalos">
			{!value.length ? <div className="wdTenue" style={{ fontSize: 13 }}>Sin Regalos</div> : null}
			{value.map((a, n) => {
				const libres = cursosPromo.filter((c) => !a.courseIds.includes(c.id));
				return (
					<div key={a.uid} className="wdAlt">
						<div className="wdAltCab">
							<b>Alternativa {n + 1}</b>
							<input className="form-control form-control-sm wdAltNombre" value={a.label} maxLength={150} placeholder={a.items.length ? nombreAlternativa({ ...a, label: "" }) : "Nombre (opcional)"}
								onChange={(e) => cambiar(a.uid, (x) => ({ ...x, label: e.target.value }))} />
							<span className="wdAltValor">{monto(sym, valorAlternativa(a))}</span>
							<button type="button" className="wdLink" disabled={value.length >= MAX_ALTERNATIVAS} onClick={() => duplicar(a)}>Duplicar</button>
							<button type="button" className="wdLink" onClick={() => borrar(a)}>Borrar</button>
						</div>
						<div className="wdChips" style={{ marginTop: 4 }}>
							<span className="wdTenue" style={{ fontSize: 13 }}>Cursos:</span>
							{!a.courseIds.length ? <span className="wdChip wdChipAcento">Todos los Cursos de la Promo</span> : a.courseIds.map((id) => (
								<button key={"c" + id} type="button" className={"wdChip wdChipBoton " + (cubre.has(id) || !cursosPromo.length ? "wdChipFilo" : "wdChipOro")} title="Quitar"
									onClick={() => cambiar(a.uid, (x) => ({ ...x, courseIds: x.courseIds.filter((c) => c !== id) }))}>
									{nombreCurso(a, id)}{cubre.has(id) || !cursosPromo.length ? "" : " (la promo ya no lo cubre)"} ✕</button>
							))}
							{libres.length ? (
								<select className="form-select form-select-sm wdAltCurso" value="" onChange={(e) => { const id = Number(e.target.value); if (id) cambiar(a.uid, (x) => ({ ...x, courseIds: [...x.courseIds, id] })); }}>
									<option value="">{a.courseIds.length ? "+ Otro Curso" : "Solo Para un Curso…"}</option>
									{libres.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
								</select>
							) : null}
						</div>
						{a.items.length ? (
							<table className="wdTabla wdAltTabla">
								<thead><tr><th>Producto</th><th style={{ width: 80 }}>Cantidad</th><th style={{ width: 110, textAlign: "right" }}>Precio</th><th style={{ width: 120, textAlign: "right" }}>Total</th><th style={{ width: 28 }} /></tr></thead>
								<tbody>
									{a.items.map((i) => (
										<tr key={"i" + i.articleId}>
											<td>{i.name}{i.sku ? <small className="wdTenue"> · {i.sku}</small> : null}</td>
											<td><input className="form-control form-control-sm" type="number" min={1} max={MAX_CANTIDAD} step={1} value={String(i.quantity)} style={{ width: 70 }}
												onChange={(e) => { const q = Math.max(0, Math.min(MAX_CANTIDAD, Math.floor(Number(e.target.value) || 0))); cambiar(a.uid, (x) => ({ ...x, items: x.items.map((y) => (y.articleId === i.articleId ? { ...y, quantity: q } : y)) })); }} /></td>
											<td style={{ textAlign: "right" }}>{i.price != null ? monto(sym, i.price) : <span className="wdOro">Sin Precio</span>}</td>
											<td style={{ textAlign: "right" }}>{monto(sym, (Number(i.price) || 0) * (Number(i.quantity) || 0))}</td>
											<td><button type="button" className="wdLink" title="Quitar producto" onClick={() => cambiar(a.uid, (x) => ({ ...x, items: x.items.filter((y) => y.articleId !== i.articleId) }))}>✕</button></td>
										</tr>
									))}
								</tbody>
							</table>
						) : <div className="wdOro" style={{ fontSize: 13, marginTop: 4 }}>Agrega al menos un producto.</div>}
						{a.items.length < MAX_PRODUCTOS ? <BuscarProducto campusId={campusId} sym={sym} onPick={(it) => agregarProducto(a, it)} /> : null}
					</div>
				);
			})}
			<div className="wdOpciones" style={{ marginTop: 8 }}>
				<button type="button" className="wdBoton" disabled={value.length >= MAX_ALTERNATIVAS} onClick={agregar}>Agregar Alternativa</button>
				<button type="button" className="wdBoton" disabled={sugiriendo || value.length >= MAX_ALTERNATIVAS} onClick={sugerir}>{sugiriendo ? "Buscando…" : "Sugerir Kits por Curso"}</button>
			</div>
			{aviso ? <div className="wdNota" style={{ fontSize: 13 }}>{aviso}</div> : null}
		</div>
	);
};

export default RegalosPromo;
